import { CRITERIOS_FILA, configuracaoFabricaSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { BarraTitulo, Formulario, aoSubmeter } from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Campo, Input, Select } from '@/components/ui/campo'
import { EsqueletoLinhas } from '@/components/ui/estado'
import type { ErrosFormulario } from '@/lib/formulario'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/configuracoes')({ component: Configuracoes })

const ROTULO: Record<(typeof CRITERIOS_FILA)[number], string> = {
  PRAZO: 'Por prazo de entrega (sem prazo vai para o fim)',
  ENTRADA: 'Por ordem de lançamento',
}

function Configuracoes() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const config = useQuery(trpc.cadastros.configuracao.obter.queryOptions())
  const [erros, setErros] = useState<ErrosFormulario>({})
  const salvar = useMutation(
    trpc.cadastros.configuracao.salvar.mutationOptions({
      onSuccess: () =>
        qc.invalidateQueries({ queryKey: trpc.cadastros.configuracao.obter.queryKey() }),
    }),
  )
  const submit = aoSubmeter({
    schema: configuracaoFabricaSchema,
    opcionais: ['whatsappSuporte'],
    numericos: ['toleranciaDeslocamentoMin', 'esperaFimTurnoMin'],
    setErros,
    sucesso: 'Configuração salva.',
    onValido: (d) => salvar.mutateAsync(d),
  })

  return (
    <>
      <BarraTitulo
        titulo="Configurações da fábrica"
        descricao="Parâmetros operacionais. Pesos, faixas e curva de experiência ficam em Desempenho › Avaliação."
      />
      <div className="max-w-xl card p-6">
        {config.isPending && <EsqueletoLinhas linhas={5} rotulo="Carregando a configuração" />}
        {config.data && (
          <Formulario id="form-config" onSubmit={submit} erros={erros}>
            <Campo
              label="Ordem da fila nos postos"
              erro={erros.criterioFila}
              dica="A PCP sempre pode fixar uma prioridade manual por ficha, que vence este critério."
            >
              {(p) => (
                <Select {...p} name="criterioFila" defaultValue={config.data.criterioFila}>
                  {CRITERIOS_FILA.map((c) => (
                    <option key={c} value={c}>
                      {ROTULO[c]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
            <Campo
              label="Fuso horário da fábrica"
              erro={erros.fusoHorario}
              dica='Define "o dia" do resumo do operador e das consultas por período (ex.: America/Sao_Paulo).'
            >
              {(p) => <Input {...p} name="fusoHorario" defaultValue={config.data.fusoHorario} />}
            </Campo>
            <Campo
              label="WhatsApp do suporte"
              erro={erros.whatsappSuporte}
              dica="Com DDI e DDD, ex.: 55 16 99999-0000. Aparece como botão Suporte no posto e na gestão. Vazio desliga."
            >
              {(p) => (
                <Input
                  {...p}
                  name="whatsappSuporte"
                  inputMode="tel"
                  defaultValue={config.data.whatsappSuporte ?? ''}
                />
              )}
            </Campo>
            <Campo
              label="Deslocamento para ajudar outro posto (minutos)"
              erro={erros.toleranciaDeslocamentoMin}
              dica="Ida e volta aceitas antes e depois de cada ajuda confirmada. A pausa 'ajudando outro posto' fora disso não conta como produtiva."
            >
              {(p) => (
                <Input
                  {...p}
                  name="toleranciaDeslocamentoMin"
                  type="number"
                  min={0}
                  max={60}
                  defaultValue={config.data.toleranciaDeslocamentoMin}
                />
              )}
            </Campo>
            <Campo
              label="Espera no fim do turno (minutos)"
              erro={erros.esperaFimTurnoMin}
              dica="No fim do turno o tablet pergunta 'Vai continuar?'. Sem resposta nesse tempo, a ficha pausa com 'Fim do expediente' no horário do fim do turno e o tablet sai."
            >
              {(p) => (
                <Input
                  {...p}
                  name="esperaFimTurnoMin"
                  type="number"
                  min={1}
                  max={120}
                  defaultValue={config.data.esperaFimTurnoMin}
                />
              )}
            </Campo>
            <Button type="submit" className="self-start" carregando={salvar.isPending}>
              Salvar
            </Button>
          </Formulario>
        )}
      </div>
    </>
  )
}
